import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, User, Eye, EyeOff, FileText, Download, Users, Building2, Globe, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import vietnamLogo from "../assets/vietnam-logo-trimmed.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username, password);
      navigate(user.role === "admin" ? "/admin" : "/client");
    } catch (err) {
      setError(err.message || "Invalid username or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* ─── Left Panel ─────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 bg-white p-8 xl:p-12 flex-col justify-between">
        <div>
          <div className="mb-8">
            <div className="mb-6">
              <img src={vietnamLogo} alt="Vietnam Airlines" className="h-[70px] w-auto" />
            </div>
            <h2 className="text-3xl xl:text-4xl font-bold text-gray-900 leading-tight">
              WELCOME TO VIETNAM AIRLINES'<br />
              <span className="text-[#367588]">CORPORATE INVOICE PORTAL</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            {[
              { icon: Building2, title: "Register as a business", desc: "Corporate account setup" },
              { icon: Users,     title: "Manage users",           desc: "Add & control access" },
              { icon: Download,  title: "Download invoices",      desc: "Access tax documents" },
              { icon: FileText,  title: "View transactions",      desc: "Track payment history" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-[#367588]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-[#367588]" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">{title}</h3>
                  <p className="text-xs text-gray-600">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Important Notice</h3>
                <ul className="space-y-1.5 text-xs text-blue-800">
                  <li>• Tax invoices will be processed as per details provided at the time of booking and will be emailed within four days.</li>
                  <li>• The Vietnam Airlines Invoice Portal can be accessed for invoices that have not been received or for revised invoices.</li>
                  <li>• For errors, please send queries to <span className="font-semibold">support@vietnamairlines.com</span></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-bold text-[#367588] mb-3">Recent Updates</h3>
            <div className="space-y-3">
              <div className="border-l-4 border-[#367588] pl-4">
                <h4 className="font-semibold text-gray-900 text-sm">Enhanced Security Features</h4>
                <p className="text-xs text-gray-600 mt-1">Two-factor authentication now available for all corporate accounts.</p>
              </div>
              <div className="border-l-4 border-[#367588] pl-4">
                <h4 className="font-semibold text-gray-900 text-sm">Bulk Download Available</h4>
                <p className="text-xs text-gray-600 mt-1">Download multiple invoices at once using the new bulk download feature.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Powered by <span className="font-semibold text-[#367588]">Vietnam Airlines Digital</span> and Tech<br />
            ©Vietnam Airlines 2024. All rights reserved
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Contact: <a href="mailto:support@vietnamairlines.com" className="text-[#367588] hover:underline">support@vietnamairlines.com</a>
          </p>
        </div>
      </div>

      {/* ─── Right Panel ────────────────────────────────────────────── */}
      <div
        className="w-full lg:w-1/2 xl:w-2/5 relative bg-cover bg-center flex items-center justify-center p-6"
        style={{ backgroundImage: `linear-gradient(rgba(54,117,136,0.75),rgba(42,90,107,0.85)),url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1080&q=80')` }}
      >
        <div className="lg:hidden absolute top-6 left-6">
          <img src={vietnamLogo} alt="Vietnam Airlines" className="h-[67px] w-auto" />
        </div>

        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header bar */}
            <div className="bg-[#367588] px-6 py-4">
              <h2 className="text-white font-bold text-lg text-center tracking-wide">Sign In</h2>
            </div>

            {/* Sign In Form */}
            <div className="p-6 sm:p-8">
              <form onSubmit={handleSignIn} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      placeholder="Enter your username"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#367588] focus:border-transparent outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
                )}

                <div className="text-right">
                  <a href="#" className="text-sm text-[#367588] hover:underline font-medium">Forgot Password?</a>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#367588] text-white py-3 rounded-lg font-semibold hover:bg-[#2a5a6b] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Signing in...
                    </>
                  ) : "Login"}
                </button>

                <div className="flex items-center justify-center gap-4 pt-1 text-sm">
                  <a href="#" className="text-[#367588] hover:underline">Need Help?</a>
                  <span className="text-gray-300">|</span>
                  <a href="#" className="text-[#367588] hover:underline">FAQ</a>
                </div>
              </form>

              {/* Demo credentials */}
              {/* <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                <p className="font-semibold mb-1">Demo credentials:</p>
                <p>Admin &nbsp;— username: <span className="font-mono font-semibold">admin</span> &nbsp;/ password: <span className="font-mono font-semibold">Admin@1234</span></p>
                <p>Client — username: <span className="font-mono font-semibold">client</span> / password: <span className="font-mono font-semibold">Client@1234</span></p>
              </div> */}

              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-center gap-2 text-xs text-gray-500">
                <Shield className="w-4 h-4 text-green-600" />
                <span>Secure enterprise login with 256-bit encryption</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}